/**
 * ChangePassword — secure password-change card on the Profile page.
 * Requires current password; new password must be 6+ chars.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, Eye, EyeOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChangePassword = () => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!current || !next) {
      toast.error("Fill in both fields");
      return;
    }
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("New passwords don't match");
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/user/change-password`,
        { current_password: current, new_password: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Password updated. You're all set.");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to update password";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-card border-border" data-testid="change-password-card">
      <CardHeader>
        <CardTitle className="text-xl font-heading text-white flex items-center gap-2">
          <Lock className="w-5 h-5 text-primary" /> Change Password
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-3">
          <Input
            type={showPw ? "text" : "password"}
            placeholder="Current password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            data-testid="current-password-input"
            autoComplete="current-password"
          />
          <Input
            type={showPw ? "text" : "password"}
            placeholder="New password (6+ chars)"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            data-testid="new-password-input"
            autoComplete="new-password"
          />
          <Input
            type={showPw ? "text" : "password"}
            placeholder="Confirm new password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="confirm-password-input"
            autoComplete="new-password"
          />

          <div className="flex items-center justify-between pt-1">
            <button
              type="button"
              onClick={() => setShowPw((s) => !s)}
              className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40 hover:text-[#FF4500] flex items-center gap-1.5"
              data-testid="toggle-show-password"
            >
              {showPw ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              {showPw ? "Hide" : "Show"}
            </button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider rounded-none"
              data-testid="change-password-btn"
            >
              {saving ? "Saving..." : "Update Password"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default ChangePassword;
